/**
 * inferDateFromLabel — turn an LLM-shaped schedule label ("this weekend",
 * "tomorrow", "monday") into a concrete future Date.
 *
 * Lives outside ReflectScreen because:
 *   1. it's a pure function (no React state, no I/O) so it tests cleanly
 *   2. the same fallback heuristic will be needed by the Today focus card
 *      and the chat Action Engine in CLCD phase 4 (route_reveal service)
 *
 * Contract:
 *   - empty / unrecognised label → returns `null` (caller falls back to
 *     its own default, today/tomorrow). NEVER returns "tomorrow" — that
 *     would re-introduce the dogfood bug where "this weekend" silently
 *     became Thursday.
 *   - weekend keywords ("weekend", "this weekend", "the weekend") → the
 *     next Saturday on or after `now`. If `now` is already Sat/Sun the
 *     same-day Saturday-pivot is returned (so "this weekend" on Sunday
 *     still feels right — see test cases).
 *   - "today" / "tonight" → today (same date as now), preserving hours.
 *   - "tomorrow" → today + 1 day.
 *   - Weekday names (monday..sunday, case-insensitive, anywhere in the
 *     label) → the next occurrence of that weekday strictly AFTER today.
 *     Same-day matches resolve to one week out — a user saying "monday"
 *     on a Monday clearly means next Monday.
 *
 * Returned Date is at 00:00:00 local time on the target day; caller is
 * expected to apply preferred_start_hour / day_start_hour before
 * serialising.
 */

const WEEKDAY_NAMES: ReadonlyArray<string> = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

function startOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function addDays(d: Date, days: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
}

/** Days until the next occurrence of `targetDay` (0=Sun .. 6=Sat).
 *  When `inclusive` is true and today is already the target day, returns 0.
 *  When false, returns 7 (same weekday next week). */
function daysUntilWeekday(
  fromDay: number,
  targetDay: number,
  inclusive: boolean,
): number {
  const diff = (targetDay - fromDay + 7) % 7;
  if (diff === 0 && !inclusive) return 7;
  return diff;
}

export function inferDateFromLabel(
  label: string | null | undefined,
  now: Date,
): Date | null {
  if (!label) return null;
  const lower = label.toLowerCase().trim();
  if (lower.length === 0) return null;

  const today = startOfDay(now);

  // Weekend — "this weekend", "the weekend", "weekend".
  // Pivot to Saturday. Edge case: if today IS Saturday, return today;
  // if today IS Sunday, return today (Sunday is still "the weekend"
  // — pulling it forward to yesterday's Saturday would be wrong).
  if (/\bweekend\b/.test(lower)) {
    const dow = today.getDay();
    if (dow === 6 || dow === 0) return today; // Sat or Sun
    const days = daysUntilWeekday(dow, 6, true); // next Saturday
    return addDays(today, days);
  }

  // Today / tonight — same calendar day.
  if (/\b(today|tonight|this evening|this afternoon|this morning)\b/.test(lower)) {
    return today;
  }

  // Tomorrow.
  if (/\btomorrow\b/.test(lower)) {
    return addDays(today, 1);
  }

  // Weekday name — pick the next future occurrence (exclude today, so
  // "monday" on Monday means next Monday).
  for (let i = 0; i < WEEKDAY_NAMES.length; i++) {
    const name = WEEKDAY_NAMES[i];
    const re = new RegExp(`\\b${name}\\b`);
    if (re.test(lower)) {
      const days = daysUntilWeekday(today.getDay(), i, false);
      return addDays(today, days);
    }
  }

  return null;
}
